import React, { useState, useEffect } from 'react';
import { 
  Target, 
  Trophy, 
  DollarSign, 
  TrendingUp, 
  Shield, 
  Zap, 
  Star,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Activity,
  Clock,
  Award,
  Layers,
  X
} from 'lucide-react';
import FuturesTradingForms from './FuturesTradingForms';
import CFDTradingForms from './CFDTradingForms';
import FuturesMyOrders from './FuturesMyOrders';
import SearchablePairSelector from './SearchablePairSelector';
import TradingChart from './TradingChart';
import { MarketData, DatabaseRobotState } from '../hooks/useDatabase';
import { PropPosition } from '../hooks/usePropFirmTrading';

interface Challenge {
  id: string;
  name: string;
  cost: number;
  accountSize: number;
  profitTarget: number;
  maxDrawdown: number;
  timeLimit: number; // in days
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert' | 'Elite' | 'Master';
  color: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}

interface PropFirmChallengePageProps {
  currentPrice: number;
  usdtBalance: number;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  propPositions: PropPosition[];
  propOrders: any[];
  propPositionHistory?: any[];
  marketData: MarketData[];
  user: any;
  robotState: DatabaseRobotState;
  onUpdateBalances: (updates: { usdt_balance?: number }) => void;
  onAddTransaction: (transaction: any) => void;
  onUpdateRobotState: (updates: Partial<DatabaseRobotState>) => void;
  onPropChallengeTrade: (
    challengeId: string,
    symbol: string,
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit'
  ) => void;
  onClosePropPosition: (positionId: string, challengeId: string) => void;
  onCancelPropOrder: (orderId: string, challengeId: string) => void;
  onCancelAllPropOrders?: (challengeId: string) => Promise<boolean>;
  closeAllPropPositions?: (challengeId: string) => Promise<boolean>;
  ticker: any;
  tickers: any;
  
}

const PropFirmChallengePage: React.FC<PropFirmChallengePageProps> = ({
  currentPrice,
  usdtBalance,
  selectedPair,
  setSelectedPair,
  propPositions,
  propOrders,
  propPositionHistory = [],
  marketData,
  user,
  robotState,
  onUpdateBalances,
  onAddTransaction,
  onUpdateRobotState,
  onPropChallengeTrade,
  onClosePropPosition,
  onCancelPropOrder,
  onCancelAllPropOrders,
  closeAllPropPositions,
  ticker,
  tickers,
  
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingChallenge, setPendingChallenge] = useState<Challenge | null>(null);
  const [tradingMode, setTradingMode] = useState<'crypto' | 'cfd'>('crypto');
  const [activeTab, setActiveTab] = useState<'positions' | 'orders' | 'history'>('positions');
  const [lastSelectedPair, setLastSelectedPair] = useState<{crypto: string, cfd: string}>({
    crypto: 'BTCUSDT',
    cfd: 'AAPL'
  });
  const [showChallengeOutcomeModal, setShowChallengeOutcomeModal] = useState(false);
  const [challengeOutcome, setChallengeOutcome] = useState<{
    status: 'won' | 'lost' | 'expired' | 'cancelled';
    message: string;
    profit: number;
  } | null>(null);

  // Set default pair when trading mode changes
  useEffect(() => {
    // When trading mode changes, set the appropriate pair for that mode
    if (tradingMode === 'crypto') {
      // If current pair isn't a crypto pair, set to the last selected crypto pair
      if (!selectedPair.endsWith('USDT')) {
        setSelectedPair(lastSelectedPair.crypto);
      }
    } else if (tradingMode === 'cfd') {
      // If current pair isn't a CFD instrument, set to the last selected CFD pair
      if (selectedPair.endsWith('USDT')) {
        setSelectedPair(lastSelectedPair.cfd);
      }
    }
  }, [tradingMode]);

  // Save the last selected pair for each mode
  useEffect(() => {
    if (selectedPair.endsWith('USDT')) {
      setLastSelectedPair(prev => ({...prev, crypto: selectedPair}));
    } else {
      setLastSelectedPair(prev => ({...prev, cfd: selectedPair}));
    }
  }, [selectedPair]);

  // Check for challenge outcome when robotState changes
  useEffect(() => {
    // Guard against undefined robotState
    if (!robotState) return;
    
    const currentChallenge = getActiveChallenge();
    if (robotState.challenge_status && robotState.challenge_status !== 'active' && currentChallenge) {
      // Calculate profit
      const profit = robotState.challenge_account_balance - robotState.challenge_initial_balance;
      
      // Set challenge outcome
      setChallengeOutcome({
        status: robotState.challenge_status as 'won' | 'lost' | 'expired' | 'cancelled',
        message: robotState.challenge_status === 'won' 
          ? `Congratulations! You've reached the profit target of $${currentChallenge.profitTarget}.` 
          : robotState.challenge_status === 'lost'
          ? `Challenge lost. You've exceeded the maximum drawdown of $${currentChallenge.maxDrawdown}.`
          : robotState.challenge_status === 'expired'
          ? `Challenge expired. The ${currentChallenge.timeLimit} day time limit has been reached.`
          : 'Challenge cancelled.',
        profit
      });
      
      // Show outcome modal
      setShowChallengeOutcomeModal(true);
    }
  }, [robotState.challenge_status, robotState.active_challenge_id]);

  const challenges: Challenge[] = [
    {
      id: 'starter',
      name: 'Starter Challenge',
      cost: 150,
      accountSize: 10000,
      profitTarget: 800, // 8%
      maxDrawdown: 500, // 5%
      timeLimit: 30,
      description: 'Perfect for beginners to test their trading skills',
      difficulty: 'Beginner',
      color: 'from-green-500 to-emerald-600',
      icon: Shield
    },
    {
      id: 'bronze',
      name: 'Bronze Challenge',
      cost: 300,
      accountSize: 25000,
      profitTarget: 2000, // 8%
      maxDrawdown: 1250, // 5%
      timeLimit: 30,
      description: 'Step up your game with a larger account',
      difficulty: 'Intermediate',
      color: 'from-amber-600 to-orange-600',
      icon: Trophy
    },
    {
      id: 'silver',
      name: 'Silver Challenge',
      cost: 600,
      accountSize: 50000,
      profitTarget: 4000, // 8%
      maxDrawdown: 2500, // 5%
      timeLimit: 30,
      description: 'Serious trading with professional tools',
      difficulty: 'Advanced',
      color: 'from-slate-400 to-slate-600',
      icon: Star
    },
    {
      id: 'gold',
      name: 'Gold Challenge',
      cost: 1200,
      accountSize: 100000,
      profitTarget: 8000, // 8%
      maxDrawdown: 5000, // 5%
      timeLimit: 30,
      description: 'Elite level trading challenge',
      difficulty: 'Expert',
      color: 'from-yellow-500 to-amber-600',
      icon: Award
    },
    {
      id: 'platinum',
      name: 'Platinum Challenge',
      cost: 2400,
      accountSize: 200000,
      profitTarget: 16000, // 8%
      maxDrawdown: 10000, // 5%
      timeLimit: 30,
      description: 'For experienced professional traders',
      difficulty: 'Elite',
      color: 'from-purple-500 to-indigo-600',
      icon: Zap
    },
    {
      id: 'diamond',
      name: 'Diamond Challenge',
      cost: 4800,
      accountSize: 400000,
      profitTarget: 32000, // 8%
      maxDrawdown: 20000, // 5%
      timeLimit: 30,
      description: 'The ultimate trading challenge for masters',
      difficulty: 'Master',
      color: 'from-cyan-500 to-blue-600',
      icon: Layers
    }
  ];

  // Get the current active challenge from robotState
  const getActiveChallenge = React.useCallback((): Challenge | null => {
    if (!robotState || !robotState.active_challenge_id) return null;
    return challenges.find(c => c.id === robotState.active_challenge_id) || null;
  }, [robotState.active_challenge_id]);

  const selectedChallenge = React.useMemo(() => getActiveChallenge(), [getActiveChallenge]);
  const challengeAccountBalance = robotState?.challenge_account_balance || 0;

  // Calculate total unrealized PnL from all prop positions
  const totalUnrealizedPnl = (propPositions ?? []).reduce((sum, position) => sum + (position.unrealizedPnl || 0), 0);
  
  // Calculate total margin used from all prop positions
  const totalMarginUsed = (propPositions ?? []).reduce((sum, position) => sum + (position.margin || 0), 0);

  const handleSelectChallenge = (challenge: Challenge) => {
    setPendingChallenge(challenge);
    setShowConfirmModal(true);
  };

  const confirmChallenge = async () => {
    if (!pendingChallenge) return;

    if (pendingChallenge.cost > usdtBalance) {
      alert('Insufficient balance to start this challenge');
      return;
    }

    // Deduct cost from main balance
    await onUpdateBalances({ usdt_balance: usdtBalance - pendingChallenge.cost });
    
    // Set up challenge in robotState
    await onUpdateRobotState({
      active_challenge_id: pendingChallenge.id,
      challenge_initial_balance: pendingChallenge.accountSize,
      challenge_account_balance: pendingChallenge.accountSize,
      challenge_profit_target: pendingChallenge.profitTarget,
      challenge_max_drawdown: pendingChallenge.maxDrawdown,
      challenge_time_limit: pendingChallenge.timeLimit,
      challenge_start_date: new Date().toISOString(),
      challenge_status: 'active'
    });
    
    // Close modal
    setShowConfirmModal(false);
    setPendingChallenge(null);
  };

  const exitChallenge = async () => {
    if (selectedChallenge) {
      // Return remaining balance (if any) to main account
      await onUpdateBalances({ usdt_balance: usdtBalance + challengeAccountBalance });
      
      // Update challenge state in robotState to cancelled
      await onUpdateRobotState({
        active_challenge_id: null,
        challenge_account_balance: 0,
        challenge_profit_target: 0,
        challenge_max_drawdown: 0,
        challenge_time_limit: 0,
        challenge_initial_balance: 0,
        challenge_start_date: null,
        challenge_status: 'cancelled'
      });
    }
  };

  // Calculate challenge progress
  const calculateProgress = () => {
    if (!selectedChallenge) return { profit: 0, profitPercentage: 0, drawdown: 0, drawdownPercentage: 0 };
    
    const profit = totalUnrealizedPnl;
    const profitPercentage = (profit / selectedChallenge.accountSize) * 100;
    
    // Calculate maximum drawdown as the difference between initial balance and current balance
    const currentBalance = selectedChallenge.accountSize + totalUnrealizedPnl;
    const drawdown = Math.max(0, selectedChallenge.accountSize - currentBalance);
    const drawdownPercentage = (drawdown / selectedChallenge.accountSize) * 100;
    
    return { profit, profitPercentage, drawdown, drawdownPercentage };
  };

  const progress = calculateProgress();

  // Determine which market data to use based on trading mode and selected pair
  const getCurrentMarketData = () => {
    // Use crypto market data or fallback to current price
    const cryptoMarket = marketData.find(data => data.symbol === selectedPair);
    return cryptoMarket || { symbol: selectedPair, price: currentPrice, volume_24h: 0, change_24h: 0, timestamp: new Date().toISOString() };
  };

  const currentMarketData = getCurrentMarketData();

  // Handle trades with challenge balance
  const handleChallengeTrade = async (
    symbol: string,
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType: 'market' | 'limit' = 'market'
  ) => {
    const price = currentMarketData?.price || currentPrice;
    const notionalValue = amount * price;
    const requiredMargin = notionalValue / leverage;
    
    // Check if there's enough available balance (account balance + unrealized PnL - margin used)
    const availableBalance = challengeAccountBalance + totalUnrealizedPnl - totalMarginUsed;
    
    if (requiredMargin > availableBalance) {
      alert('Insufficient challenge account balance');
      return;
    }

    // Execute the trade using the prop challenge handler
    onPropChallengeTrade(
      selectedChallenge.id,
      symbol,
      side, 
      amount, 
      leverage, 
      marginType, 
      stopLoss, 
      takeProfit,
      orderType
    );
  };

  if (!selectedChallenge) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="relative z-10 p-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-blue-500/25">
              <Target size={32} className="text-white" />
            </div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-4">
              Prop Firm Challenges
            </h1>
            <p className="text-slate-400 text-xl max-w-3xl mx-auto">
              Prove your trading skills and earn funding. Choose your challenge level and start trading with virtual capital.
            </p>
          </div>

          {/* Available Balance */}
          <div className="text-center mb-12">
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl max-w-md mx-auto">
              <div className="text-slate-400 text-sm mb-2">Available Balance</div>
              <div className="text-3xl font-bold text-emerald-400">${usdtBalance.toLocaleString()}</div>
            </div>
          </div>

          {/* Challenges Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {challenges.map((challenge) => {
              const Icon = challenge.icon;
              const canAfford = challenge.cost <= usdtBalance;
              
              return (
                <div
                  key={challenge.id}
                  className={`bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl transition-all duration-300 hover:transform hover:scale-105 ${
                    canAfford ? 'hover:border-slate-600/50 cursor-pointer' : 'opacity-60'
                  }`}
                  onClick={() => canAfford && handleSelectChallenge(challenge)}
                >
                  {/* Challenge Header */}
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${challenge.color} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <Icon size={28} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{challenge.name}</h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      challenge.difficulty === 'Beginner' ? 'bg-green-500/20 text-green-400' :
                      challenge.difficulty === 'Intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
                      challenge.difficulty === 'Advanced' ? 'bg-orange-500/20 text-orange-400' :
                      challenge.difficulty === 'Expert' ? 'bg-red-500/20 text-red-400' :
                      challenge.difficulty === 'Elite' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-cyan-500/20 text-cyan-400'
                    }`}>
                      {challenge.difficulty}
                    </span>
                  </div>

                  {/* Challenge Details */}
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Entry Fee</span>
                      <span className="text-white font-bold">${challenge.cost.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Account Size</span>
                      <span className="text-emerald-400 font-bold">${challenge.accountSize.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Profit Target</span>
                      <span className="text-blue-400 font-bold">${challenge.profitTarget.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Max Drawdown</span>
                      <span className="text-red-400 font-bold">${challenge.maxDrawdown.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Time Limit</span>
                      <span className="text-white font-bold">{challenge.timeLimit} days</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-slate-400 text-sm mb-6 text-center">{challenge.description}</p>

                  {/* Action Button */}
                  <button
                    disabled={!canAfford}
                    className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                      canAfford
                        ? `bg-gradient-to-r ${challenge.color} hover:shadow-lg transform hover:scale-105 text-white`
                        : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    {canAfford ? (
                      <>
                        Start Challenge
                        <ArrowRight size={16} />
                      </>
                    ) : (
                      'Insufficient Balance'
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Info Section */}
          <div className="max-w-4xl mx-auto mt-16">
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">How It Works</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <DollarSign size={24} className="text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">1. Pay Entry Fee</h4>
                  <p className="text-slate-400 text-sm">Choose your challenge level and pay the entry fee to get started.</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <BarChart3 size={24} className="text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">2. Trade & Profit</h4>
                  <p className="text-slate-400 text-sm">Use the virtual capital to trade and reach your profit target.</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Trophy size={24} className="text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">3. Get Funded</h4>
                  <p className="text-slate-400 text-sm">Pass the challenge and receive funding to trade with real capital.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && pendingChallenge && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl max-w-md w-full p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Confirm Challenge</h3>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="text-center mb-6">
                <div className={`w-16 h-16 bg-gradient-to-r ${pendingChallenge.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <pendingChallenge.icon size={28} className="text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">{pendingChallenge.name}</h4>
                <p className="text-slate-400 text-sm">{pendingChallenge.description}</p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-slate-400">Entry Fee:</span>
                  <span className="text-white font-bold">${pendingChallenge.cost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Account Size:</span>
                  <span className="text-emerald-400 font-bold">${pendingChallenge.accountSize.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Your Balance:</span>
                  <span className="text-white font-bold">${usdtBalance.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">After Payment:</span>
                  <span className="text-white font-bold">${(usdtBalance - pendingChallenge.cost).toLocaleString()}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmChallenge}
                  className={`flex-1 bg-gradient-to-r ${pendingChallenge.color} text-white py-3 rounded-lg font-medium transition-all duration-300 shadow-lg`}
                >
                  Start Challenge
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Challenge Trading Interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white relative overflow-hidden pb-8">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 p-8">
        {/* Challenge Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 bg-gradient-to-r ${selectedChallenge.color} rounded-xl flex items-center justify-center shadow-lg`}>
              <selectedChallenge.icon size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{selectedChallenge.name}</h1>
              <p className="text-slate-400">Challenge in progress</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Trading Mode Toggle */}
            <div className="flex bg-slate-800/50 rounded-xl p-1 border border-slate-600/30">
              <button
                onClick={() => setTradingMode('crypto')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  tradingMode === 'crypto' 
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                Crypto
              </button>
              <button
                onClick={() => setTradingMode('cfd')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  tradingMode === 'cfd'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                }`}
              >
                CFDs
              </button>
            </div>

            {/* Pair Selector */}
            <div className="relative">
              <SearchablePairSelector
                selectedPair={selectedPair || (tradingMode === 'crypto' ? 'BTCUSDT' : 'AAPL')}
                onPairSelect={(pair: string) => {
                  setSelectedPair(pair);
                  // Also update the last selected pair for this mode
                  if (pair.endsWith('USDT')) {
                    setLastSelectedPair(prev => ({...prev, crypto: pair}));
                  } else {
                    setLastSelectedPair(prev => ({...prev, cfd: pair}));
                  }
                }}
                tradingMode={tradingMode === 'cfd' ? 'cfd' : 'futures'}
              />
              {/* Debug info - remove in production */}
              <div className="hidden absolute -bottom-6 left-0 text-xs text-slate-500">
                Mode: {tradingMode}, Pair: {selectedPair}
              </div>
            </div>

            <button
              onClick={exitChallenge}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Exit Challenge
            </button>
          </div>
        </div>

        {/* Challenge Progress */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign size={20} className="text-emerald-400" />
              <span className="text-slate-400 text-sm">Total Account Value</span>
            </div>
            <div className="text-2xl font-bold text-white">${(selectedChallenge.accountSize + totalUnrealizedPnl).toLocaleString()}</div>
            <div className="text-slate-400 text-sm">Initial: ${selectedChallenge.accountSize.toLocaleString()}</div>
          </div>

          <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp size={20} className={progress.profit >= 0 ? 'text-emerald-400' : 'text-red-400'} />
              <span className="text-slate-400 text-sm">P&L</span>
            </div>
            <div className={`text-2xl font-bold ${progress.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {progress.profit >= 0 ? '+' : ''}${progress.profit.toFixed(2)}
            </div>
            <div className="text-slate-400 text-sm">
              {progress.profitPercentage >= 0 ? '+' : ''}{progress.profitPercentage.toFixed(2)}%
            </div>
          </div>

          <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Target size={20} className="text-blue-400" />
              <span className="text-slate-400 text-sm">Profit Target</span>
            </div>
            <div className="text-2xl font-bold text-white">${selectedChallenge.profitTarget.toLocaleString()}</div>
            <div className="text-slate-400 text-sm">
              {((progress.profit / selectedChallenge.profitTarget) * 100).toFixed(1)}% complete
            </div>
          </div>

         <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-lg">
           <div className="flex items-center gap-3 mb-2">
             <AlertTriangle size={20} className="text-red-400" />
             <span className="text-slate-400 text-sm">Drawdown Status</span>
           </div>
           <div className="text-2xl font-bold text-white">
             ${progress.drawdown.toFixed(2)} / ${selectedChallenge.maxDrawdown.toLocaleString()}
           </div>
           <div className="text-slate-400 text-sm">
             {progress.drawdownPercentage.toFixed(1)}% of max {(selectedChallenge.maxDrawdown / selectedChallenge.accountSize * 100).toFixed(1)}%
           </div>
         </div>
        </div>

        {/* Progress Bars */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-lg">
            <div className="flex justify-between items-center mb-3">
              <span className="text-slate-400 text-sm">Profit Progress</span>
              <span className="text-blue-400 font-medium">
                {((progress.profit / selectedChallenge.profitTarget) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(Math.max((progress.profit / selectedChallenge.profitTarget) * 100, 0), 100)}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 shadow-lg">
            <div className="flex justify-between items-center mb-3">
              <span className="text-slate-400 text-sm">Drawdown Risk</span>
              <span className="text-red-400 font-medium">
                {((progress.drawdown / selectedChallenge.maxDrawdown) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-red-500 to-pink-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((progress.drawdown / selectedChallenge.maxDrawdown) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* TradingView Chart */}
        <div className="mb-8">
          <TradingChart selectedPair={selectedPair || (tradingMode === 'crypto' ? 'BTCUSDT' : 'AAPL')} />
        </div>

        {/* Trading Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Trading Forms */}
          <div>
            {tradingMode === 'cfd' ? (
              <CFDTradingForms
                currentPrice={currentPrice}
                usdtBalance={challengeAccountBalance}
                selectedPair={selectedPair || 'AAPL'}
                isPropChallenge={true}
                challengeId={selectedChallenge.id}
                onPropChallengeTrade={onPropChallengeTrade}
              />
            ) : (
              <FuturesTradingForms
                currentBtcPrice={currentPrice}
                usdtBalance={challengeAccountBalance}
                isPropChallenge={true}
                challengeId={selectedChallenge.id}
                onPropChallengeTrade={onPropChallengeTrade}
                selectedPair={selectedPair || 'BTCUSDT'}
              />
            )}
          </div>

          {/* Positions */}
          <div>
            <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl h-full flex flex-col">
              <div className="flex items-center gap-6 mb-6">
                <h3 className="text-xl font-semibold text-cyan-400">Prop Challenge</h3>
                <div className="flex gap-6">
                  <button
                    onClick={() => setActiveTab('positions')}
                    className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
                      activeTab === 'positions' 
                        ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`}
                  >
                    Positions
                  </button>
                  <button
                    onClick={() => setActiveTab('orders')}
                    className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
                      activeTab === 'orders' 
                        ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`}
                  >
                    Open Orders
                  </button>
                  <button
                    onClick={() => setActiveTab('history')}
                    className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
                      activeTab === 'history' 
                        ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                    }`}
                  >
                    Position History
                  </button>
                </div>
              </div>
              
              {/* Assets Section */}
              <div className="bg-slate-900/30 rounded-xl p-6 mb-6 border border-slate-600/30 shadow-lg">
                <h4 className="text-cyan-400 font-semibold mb-4 text-lg">Assets</h4>
                <div className="grid grid-cols-2 gap-6 text-sm">
                  <div>
                    <span className="text-slate-400">Overall Balance:</span>
                    <div className="text-white font-mono text-lg">${(challengeAccountBalance + totalUnrealizedPnl).toFixed(2)} USD</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Available Balance:</span>
                    <div className="text-white font-mono text-lg">${(challengeAccountBalance - totalMarginUsed).toFixed(2)} USD</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Unrealized Pnl:</span>
                    <div className={`font-mono text-lg ${totalUnrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {totalUnrealizedPnl.toFixed(2)} USD
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-400">Running Position:</span>
                    <div className="text-white font-mono text-lg">{(propPositions ?? []).length}</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Burning Position:</span>
                    <div className="text-white font-mono text-lg">0</div>
                  </div>
                  <div>
                    <span className="text-slate-400">Open Orders:</span>
                    <div className="text-white font-mono text-lg">{(propOrders ?? []).length}</div>
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button 
                    onClick={() => closeAllPropPositions && selectedChallenge && closeAllPropPositions(selectedChallenge.id)}
                    disabled={!closeAllPropPositions || (propPositions ?? []).length === 0}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/25 transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    Close All Positions
                  </button>
                  <button 
                    onClick={() => onCancelAllPropOrders && selectedChallenge && onCancelAllPropOrders(selectedChallenge.id)}
                    disabled={!onCancelAllPropOrders || (propOrders ?? []).length === 0}
                    className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-red-500/25 transform hover:scale-105 flex items-center justify-center gap-2"
                  >
                    Cancel All Orders
                  </button>
                </div>
              </div>

              <div className="flex-1 flex flex-col">
                {activeTab === 'positions' && (
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between text-xs text-slate-400 mb-4 font-medium">
                      <span>Symbol</span>
                      <span>Size</span>
                      <span>Side</span>
                      <span>Entry Price</span>
                      <span>Current Price</span>
                      <span>Liq. Price</span>
                      <span>Margin</span>
                      <span>PNL (ROI)</span>
                      <span></span>
                    </div>

                    {(propPositions ?? []).length > 0 ? (
                      <div className="space-y-3 flex-1 overflow-auto">
                        {(propPositions ?? []).map((position) => (
                          <div key={position.id} className="flex items-center justify-between bg-slate-900/30 px-6 py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                            <span className="text-white font-medium">{position.symbol}</span>
                            <span className={`font-mono ${position.side === 'long' ? 'text-emerald-400' : 'text-red-400'}`}>
                              {(position.amount || 0).toFixed(4)}
                            </span>
                            <span className={`text-xs font-bold px-3 py-1 rounded-lg ${
                              position.side === 'long' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                            }`}>
                              {(position.side || 'long').toUpperCase()}
                            </span>
                            <span className="text-slate-300 font-mono">{(position.entryPrice || 0).toFixed(4)}</span>
                            <span className="text-slate-300 font-mono">
                              {(position.currentPrice || 0).toFixed(4)}
                            </span>
                            <span className="text-red-400 font-mono">{(position.liquidationPrice || 0).toFixed(2)}</span>
                            <span className="text-slate-300 font-mono">{(position.margin || 0).toFixed(2)}</span>
                            <div className="text-right">
                              <div className={`font-mono ${(position.unrealizedPnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {(position.unrealizedPnl || 0).toFixed(4)}
                              </div>
                              <div className={`text-xs ${(position.roi || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                ({(position.roi || 0).toFixed(2)}%)
                              </div>
                            </div>
                            <button 
                              onClick={() => onClosePropPosition(position.id, selectedChallenge.id)}
                              className="text-slate-400 hover:text-red-400 transition-colors p-2 hover:bg-red-400/10 rounded-lg"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
                        <Activity size={64} className="mb-6 opacity-30" />
                        <p className="text-lg">No positions found</p>
                        <p className="text-sm text-slate-600 mt-2">Your prop challenge positions will appear here</p>
                      </div>
                    )}
                  </div>
                )}
                
                {activeTab === 'orders' && (
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between text-xs text-slate-400 mb-4 font-medium">
                      <span>Date</span>
                      <span>Symbol</span>
                      <span>Type</span>
                      <span>Side</span>
                      <span>Price</span>
                      <span>Amount</span>
                      <span>Leverage</span>
                      <span>Status</span>
                      <span></span>
                    </div>
                    
                    {(propOrders ?? []).length > 0 ? (
                      <div className="space-y-3 flex-1 overflow-auto">
                        {(propOrders ?? []).map((order) => (
                          <div key={order.id} className="flex items-center justify-between bg-slate-900/30 px-6 py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                            <span className="text-slate-300 text-sm font-mono">
                              {new Date(order.created_at).toLocaleString()}
                            </span>
                            <span className="text-white font-medium">{order.symbol}</span>
                            <span className="text-slate-300 text-sm capitalize">{order.type}</span>
                            <span className={`text-xs font-bold px-3 py-1 rounded-lg ${
                              order.side === 'buy' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                            }`}>
                              {order.side.toUpperCase()}
                            </span>
                            <span className="text-slate-300 font-mono">{order.price?.toFixed(4) || 'Market'}</span>
                            <span className="text-slate-300 font-mono">{(order.amount || 0).toFixed(4)}</span>
                            <span className="text-orange-400 font-mono">{order.leverage || 1}x</span>
                            <span className="text-blue-400 text-sm">Open</span>
                            <button 
                              onClick={() => onCancelPropOrder(order.id, selectedChallenge.id)}
                              className="text-slate-400 hover:text-red-400 transition-colors p-2 hover:bg-red-400/10 rounded-lg"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
                        <Activity size={64} className="mb-6 opacity-30" />
                        <p className="text-lg">No open orders found</p>
                        <p className="text-sm text-slate-600 mt-2">Your pending orders will appear here</p>
                      </div>
                    )}
                  </div>
                )}
                
                {activeTab === 'history' && (
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between text-xs text-slate-400 mb-4 font-medium">
                      <span>Date</span>
                      <span>Symbol</span>
                      <span>Side</span>
                      <span>Entry Price</span>
                      <span>Exit Price</span>
                      <span>PnL</span>
                      <span>ROI</span>
                      <span>Duration</span>
                    </div>
                    
                    {(propPositionHistory ?? []).length > 0 ? (
                      <div className="space-y-3 flex-1 overflow-auto">
                        {(propPositionHistory ?? []).map((position) => (
                          <div key={position.id} className="flex items-center justify-between bg-slate-900/30 px-6 py-4 rounded-xl hover:bg-slate-900/50 transition-all duration-300 border border-slate-600/30 hover:border-slate-500/50 shadow-lg">
                            <span className="text-slate-300 text-sm font-mono">
                              {new Date(position.close_time || position.closeTime).toLocaleString()}
                            </span>
                            <span className="text-white font-medium">{position.symbol}</span>
                            <span className={`text-xs font-bold px-3 py-1 rounded-lg ${
                              position.side === 'long' ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'
                            }`}>
                              {position.side.toUpperCase()}
                            </span>
                            <span className="text-slate-300 font-mono">{position.entry_price?.toFixed(4) || position.entryPrice?.toFixed(4)}</span>
                            <span className="text-slate-300 font-mono">{position.exit_price?.toFixed(4) || position.exitPrice?.toFixed(4)}</span>
                            <span className={`font-mono ${position.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {position.pnl.toFixed(4)}
                            </span>
                            <span className={`flex items-center ${position.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {position.roi >= 0 ? (
                                <TrendingUp size={14} className="mr-1" />
                              ) : (
                                <TrendingDown size={14} className="mr-1" />
                              )}
                              {position.roi.toFixed(2)}%
                            </span>
                            <span className="text-slate-300 flex items-center">
                              <Clock size={14} className="mr-1 text-slate-400" />
                              {formatDuration(position.duration_seconds || position.durationSeconds)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 text-slate-500 flex-1">
                        <Activity size={64} className="mb-6 opacity-30" />
                        <p className="text-lg">No position history found</p>
                        <p className="text-sm text-slate-600 mt-2">Your closed positions will appear here</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Challenge Outcome Modal */}
      {showChallengeOutcomeModal && challengeOutcome && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-xl max-w-md w-full p-6 border border-slate-700 shadow-2xl">
            <div className="text-center mb-6">
              {challengeOutcome.status === 'won' ? (
                <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy size={40} className="text-white" />
                </div>
              ) : challengeOutcome.status === 'lost' || challengeOutcome.status === 'expired' ? (
                <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle size={40} className="text-white" />
                </div>
              ) : (
                <div className="w-20 h-20 bg-gradient-to-r from-slate-500 to-slate-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X size={40} className="text-white" />
                </div>
              )}
              
              <h3 className="text-2xl font-bold text-white mb-2">
                Challenge {challengeOutcome.status === 'won' ? 'Completed' : 
                           challengeOutcome.status === 'lost' ? 'Failed' : 
                           challengeOutcome.status === 'expired' ? 'Expired' : 'Cancelled'}
              </h3>
              
              <p className="text-slate-300 mb-6">
                {challengeOutcome.message}
              </p>
            </div>
            
            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-slate-400">Final P&L:</span>
                <span className={challengeOutcome.profit >= 0 ? "text-emerald-400 font-bold" : "text-red-400 font-bold"}>
                  {challengeOutcome.profit >= 0 ? '+' : ''}{challengeOutcome.profit.toFixed(2)} USD
                </span>
              </div>
              
              {challengeOutcome.status === 'won' && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Reward Added:</span>
                  <span className="text-emerald-400 font-bold">
                    +{selectedChallenge?.profitTarget.toFixed(2)} USD
                  </span>
                </div>
              )}
            </div>
            
            <button
              onClick={() => {
                setShowChallengeOutcomeModal(false);
                setChallengeOutcome(null);
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Format duration from seconds to human-readable format
const formatDuration = (seconds: number) => {
  if (!seconds) return 'N/A';
  
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
};

// Define combinedMarketDataForPositions
const combinedMarketDataForPositions = [];

export default PropFirmChallengePage;